import type { FmTableCellValue } from '../../../types/fm-table';
import type { FmEvidenceDescriptor } from '../FmEvidence.types';
import type { FmStoryAction, FmStoryBadge, FmStoryChapter, FmStoryChart, FmStoryComparisonMatrix, FmStoryContextControl, FmStoryContextControlOption, FmStoryCorridorTrajectory, FmStoryNotice, FmStoryRankedRow, FmStoryRankedTableInsight, FmStoryTableDescriptor, FmStoryTimelineEvent, FmStoryValueExplanation } from './elementFields.types';
import type { FmStoryActionSet, FmStoryChartMediaSlot, FmStoryImageMediaSlot, FmStoryMapMultiSelection, FmStoryMediaSlot } from './elementMedia.types';

/**
 * Typed content elements: one content job each, no arrangement and no `id`.
 *
 * Payloads reuse the field shapes the story kinds already validate, so every
 * kind's content is expressible as elements. Without an `id` an element can
 * never form the `kind` + `id` + `title` triple the nested-descriptor guard
 * rejects; arrangement belongs to the section that places the element.
 */
export const FM_ELEMENT_TYPES = [
  'copy',
  'keyFigures',
  'facts',
  'definitionList',
  'chart',
  'map',
  'image',
  'document',
  'table',
  'rankedList',
  'steps',
  'timeline',
  'notice',
  'actions',
  'provenance',
  'lens',
] as const;

export type FmElementType = (typeof FM_ELEMENT_TYPES)[number];

/**
 * The type tier an element reads at. `display` is the statement tier: a light
 * 40-64px headline or hero number for section openers and stat bands, with a
 * monospace eyebrow. It is an emphasis choice, never a size.
 */
export const FM_ELEMENT_SCALES = ['standard', 'display'] as const;
export type FmElementScale = (typeof FM_ELEMENT_SCALES)[number];

interface FmElementBase<Type extends FmElementType> {
  element: Type;
  /** Route-supplied test id; renderers derive their suffixes from it. */
  testId?: string;
}

type WithoutKeys<Keys extends PropertyKey> = { [Key in Keys]?: never };

/** Missing data names its reason instead of rendering a placeholder value. */
export interface FmElementUnavailable {
  unavailableReason: string;
}

export interface FmCopyElement extends FmElementBase<'copy'> {
  eyebrow?: string;
  heading?: string;
  lede?: string;
  body?: readonly string[];
  list?: readonly string[];
  sourceNote?: string;
  badges?: readonly FmStoryBadge[];
  /** `display` sets the heading at the statement tier; the body keeps reading size. */
  scale?: FmElementScale;
}

/**
 * The only colour a key figure may carry: a declared semantic status. A
 * figure's value is neutral otherwise; decoration never colours a number.
 */
export type FmKeyFigureStatus = 'good' | 'caution' | 'loss';

/** A declared signed change beside the value; its direction colours it. */
export interface FmKeyFigureDelta {
  label: string;
  direction: 'up' | 'down' | 'flat';
}

interface FmKeyFigureBase {
  label: string;
  unit?: string;
  footnote?: string;
  status?: FmKeyFigureStatus;
  delta?: FmKeyFigureDelta;
}

/**
 * A key figure is a number. `value` is the locale-formatted number (digits,
 * grouping, sign, decimal mark; currency or percent glyph allowed) and the
 * unit sits in `unit`; text answers such as a species or a status are facts.
 */
export type FmKeyFigure = FmKeyFigureBase &
  ({ value: string; unavailableReason?: never } | ({ value: null } & FmElementUnavailable));

export interface FmKeyFiguresElement extends FmElementBase<'keyFigures'> {
  figures: readonly [FmKeyFigure, ...FmKeyFigure[]];
  /**
   * `display` is the stat band: each value at the statement tier with its
   * label read as the sentence under it, no dividers.
   */
  scale?: FmElementScale;
}

/** Labelled facts whose value is optional context, not a headline figure. */
export interface FmFactsElement extends FmElementBase<'facts'> {
  items: readonly [FmStoryValueExplanation, ...FmStoryValueExplanation[]];
}

/** One glossary entry: the term and what it means in plain words. */
export interface FmDefinition {
  term: string;
  definition: string;
}

/** A glossary: terms as labels, each definition as regular body text. */
export interface FmDefinitionListElement extends FmElementBase<'definitionList'> {
  items: readonly [FmDefinition, ...FmDefinition[]];
}

type FmChartChrome = Omit<FmStoryChartMediaSlot, 'kind' | 'chart' | 'testId'>;
/**
 * A served chart or an unavailable one keeps the shared chrome; the corridor
 * figure carries its own caption, takeaway and legend, so it takes none.
 */
type FmChartData =
  | (FmChartChrome & { chart: FmStoryChart; corridor?: never; unavailableReason?: never })
  | ({ corridor: FmStoryCorridorTrajectory; chart?: never; unavailableReason?: never } & {
      [Key in keyof FmChartChrome]?: never;
    })
  | (FmChartChrome & { chart?: never; corridor?: never } & FmElementUnavailable);

export type FmChartElement = FmElementBase<'chart'> & FmChartData;

type FmMapMediaSlot = Exclude<FmStoryMediaSlot, FmStoryImageMediaSlot | FmStoryChartMediaSlot>;

/**
 * A map. `selection` turns on multi-select mode on a slot that supports it
 * (`MULTI_SELECT_MAP_SLOT_KEYS`): click, lasso, and a keyboard list.
 */
export type FmMapElement = FmElementBase<'map'> &
  Omit<FmMapMediaSlot, 'kind' | 'testId'> & { selection?: FmStoryMapMultiSelection };

type FmImageMediaSlotWithoutKind<Slot> = Slot extends unknown
  ? Omit<Slot, 'kind' | 'testId'>
  : never;

export type FmImageElement = FmElementBase<'image'> &
  FmImageMediaSlotWithoutKind<FmStoryImageMediaSlot>;

/** Page-navigation copy, supplied by the descriptor in the owner's language. */
export interface FmDocumentLabels {
  previousPage: string;
  nextPage: string;
  /**
   * The position printed between the buttons and announced on change, with
   * `{page}` and `{pageCount}` placeholders, e.g. "Page {page} of {pageCount}".
   * A template rather than a formatter because descriptors carry data, not functions.
   */
  pagePosition: string;
  loading: string;
  /** Said when the served file cannot be read as a PDF. */
  unreadable: string;
  /** Enlarge the page; a zoomed page scrolls inside its frame. */
  zoomIn: string;
  /** Back towards the page fitted to the frame. */
  zoomOut: string;
}

/**
 * `file` is a same-origin path or an object URL a domain hook created from the
 * bytes it fetched; the renderer reads only that, so authenticated documents
 * never leave their hook and the element itself never calls an API.
 */
type FmDocumentData =
  | { file: string; labels: FmDocumentLabels; unavailableReason?: never }
  | ({ file?: never; labels?: never } & FmElementUnavailable);

/**
 * A served PDF read in place one page at a time (react-pdf on pdf.js), because
 * an iframe or object viewer is unreliable on the phone browsers owners use.
 */
export type FmDocumentElement = FmElementBase<'document'> & {
  /** Names the document; it is the viewer's accessible name. */
  title: string;
  caption?: string;
} & FmDocumentData;

/** Top-N preview with a show-all toggle; `revealRowKey` mounts a route-return target. */
export interface FmTablePreview {
  limit: number;
  showAllLabel: string;
  showLessLabel: string;
  revealRowKey?: string;
}

/**
 * One row's inline select. `filterId` names what the select drives and is
 * stamped on the control and its options, so a route can restore focus to it.
 */
export interface FmTableSelectCell {
  filterId: string;
  ariaLabel: string;
  options: readonly [FmStoryContextControlOption, ...FmStoryContextControlOption[]];
  disabled?: boolean;
}

/**
 * A column whose cells render as inline selects. The row field holds the
 * selected value; a row without its own select cell prints that field as text.
 */
export interface FmTableSelectColumn {
  columnKey: string;
  /** Select cells keyed by row key. */
  cells: Readonly<Record<string, FmTableSelectCell>>;
  onChange: (rowKey: string, value: string) => void;
}

/** One labelled secondary fact revealed under its row. */
export interface FmTableRowDetail {
  id: string;
  label: string;
  value: FmTableCellValue;
  /** A muted line under the value saying what it means. */
  detail?: string;
}

/**
 * The one table disclosure option set. Row preview, grouping, expandable
 * details and select cells are the same table behaviours wherever a table
 * sits, so they are options here rather than table variants.
 */
export interface FmTableDisclosure {
  preview?: FmTablePreview;
  /** Row field whose text labels the group header each row sits under. */
  groupByField?: string;
  /** Labelled secondary facts revealed under each row, keyed by row key. */
  expandableRows?: Readonly<Record<string, readonly [FmTableRowDetail, ...FmTableRowDetail[]]>>;
  selectColumns?: readonly [FmTableSelectColumn, ...FmTableSelectColumn[]];
}

type PreviewKeys = 'previewLimit' | 'showAllLabel' | 'showLessLabel';

type FmTableData =
  | ({
      table: Omit<FmStoryTableDescriptor, PreviewKeys>;
      disclosure?: FmTableDisclosure;
    } & WithoutKeys<'rankedTable' | 'comparison' | 'unavailableReason'>)
  | ({
      rankedTable: Omit<FmStoryRankedTableInsight, PreviewKeys>;
      /** A ranked table previews its rows; grouping, details and select cells are plain-table options. */
      disclosure?: Pick<FmTableDisclosure, 'preview'>;
    } & WithoutKeys<'table' | 'comparison' | 'unavailableReason'>)
  | ({ comparison: FmStoryComparisonMatrix } & WithoutKeys<
      'table' | 'rankedTable' | 'unavailableReason' | 'disclosure'
    >)
  | (FmElementUnavailable & WithoutKeys<'table' | 'rankedTable' | 'comparison' | 'disclosure'>);

export type FmTableElement = FmElementBase<'table'> & { title?: string } & FmTableData;

export interface FmRankedListElement extends FmElementBase<'rankedList'> {
  title?: string;
  rows: readonly FmStoryRankedRow[];
  valueHeader?: string;
  emptyLabel?: string;
  limit?: number;
  highlightTop?: number;
}

export interface FmStepsElement extends FmElementBase<'steps'> {
  items: readonly [FmStoryChapter, ...FmStoryChapter[]];
}

export interface FmTimelineElement extends FmElementBase<'timeline'> {
  events: readonly [FmStoryTimelineEvent, ...FmStoryTimelineEvent[]];
}

export type FmNoticeElement = FmElementBase<'notice'> & Omit<FmStoryNotice, 'id' | 'testId'>;

export type FmActionsElement = FmElementBase<'actions'> &
  (
    | { primary: FmStoryAction; secondary?: readonly FmStoryAction[]; set?: never }
    | { primary?: never; secondary: readonly [FmStoryAction, ...FmStoryAction[]]; set?: never }
    | { primary?: never; secondary?: never; set: FmStoryActionSet }
  );

export interface FmProvenanceElement extends FmElementBase<'provenance'> {
  evidence: FmEvidenceDescriptor;
}

/** Switches between payloads of the same element type; the section owns the panels. */
export interface FmLensElement extends FmElementBase<'lens'> {
  control: FmStoryContextControl;
}

export type FmElement =
  | FmCopyElement
  | FmKeyFiguresElement
  | FmFactsElement
  | FmDefinitionListElement
  | FmChartElement
  | FmMapElement
  | FmImageElement
  | FmDocumentElement
  | FmTableElement
  | FmRankedListElement
  | FmStepsElement
  | FmTimelineElement
  | FmNoticeElement
  | FmActionsElement
  | FmProvenanceElement
  | FmLensElement;

export type FmElementOf<Type extends FmElementType> = Extract<FmElement, { element: Type }>;
